/**
 * Is this campaign still working?
 *
 * ONE answer, derived from remaining work — used by the Campaigns list badge AND
 * the sidebar mascot, so the two can never disagree again. They used to: the
 * badge read campaigns.status ('active'), while the sidebar asked whether any
 * rows were waiting or anything had been delivered in the last hour. A drained
 * campaign therefore showed "3/3 · Active" next to "All quiet · no active
 * campaigns".
 *
 * Derived rather than latched, which is what makes the two awkward cases free:
 *
 *   - the cron cannot resurrect a finished campaign. It writes campaigns.status,
 *     and nothing here reads that column to decide live-vs-done.
 *   - adding leads to a finished campaign re-opens it, because the new rows are
 *     non-terminal and `remaining` goes back above zero.
 *
 * A stored `completed_at` would have needed an explicit reopen path, and would
 * have been wrong for exactly as long as anyone forgot to write it.
 */
import type { CampaignStats, CampaignStatus } from './types'

export type CampaignLifecycle = 'draft' | 'paused' | 'live' | 'done'

/** Only these count as "still to send" — see 0020 for the SQL that matches. */
export const TERMINAL_QUEUE_STATUSES = ['sent', 'failed', 'canceled', 'rejected'] as const

export function isTerminalQueueStatus(status: string): boolean {
  return (TERMINAL_QUEUE_STATUSES as readonly string[]).includes(status)
}

/** The counts the decision needs. Kept structural so callers can pass a row of
 *  campaign_stats, or compute the two numbers themselves. */
export interface RemainingWork {
  /** rows that can still be delivered (non-terminal) */
  remaining?: number | string | null
  /** rows that reached a terminal state */
  processed?: number | string | null
}

const n = (v: number | string | null | undefined): number => {
  const parsed = Number(v ?? 0)
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * draft  — never launched.
 * paused — an explicit, manual hold. Deliberately NOT folded into done: the user
 *          stopped it, and it resumes to exactly where it was.
 * done   — nothing left to send, and something was actually processed.
 * live   — anything else that has been launched, including a campaign that is
 *          scheduled or has no leads yet. "Nothing queued" is not "finished".
 */
export function campaignLifecycle(
  campaign: { status: CampaignStatus },
  work: RemainingWork | null | undefined,
): CampaignLifecycle {
  if (campaign.status === 'draft') return 'draft'
  if (campaign.status === 'paused') return 'paused'

  const remaining = n(work?.remaining)
  const processed = n(work?.processed)

  // Zero remaining alone is not enough — a freshly launched campaign with no
  // leads yet also has zero, and it has not finished anything.
  if (remaining === 0 && processed > 0) return 'done'
  return 'live'
}

/** What the badge says. */
export const LIFECYCLE_LABEL: Record<CampaignLifecycle, string> = {
  draft: 'Draft',
  paused: 'Paused',
  live: 'Active',
  done: 'Done',
}

/**
 * The sidebar's count. Same function as the badge, so "N campaigns live" is by
 * construction the number of rows badged Active.
 */
export function countLive(
  stats: (CampaignStats & RemainingWork)[] | null | undefined,
): number {
  return (stats ?? []).filter((s) => campaignLifecycle(s, s) === 'live').length
}
